import { PageHero } from '@components/common';
import { getServiceCard } from '@data/serviceCards';
import {
  Boxes,
  Search,
  ClipboardCheck,
  Layers,
  ScrollText,
  Eye,
  Share2,
  CheckCircle,
  ArrowRight,
  Clock,
  Activity,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { CALENDLY_URL } from '@constants';

const offerings = [
  {
    icon: Search,
    title: 'Third-Party Risk Assessment',
    description:
      'Assess the security posture of your vendors and partners, exposing the hidden exposure that third-party relationships introduce into your environment.',
  },
  {
    icon: ClipboardCheck,
    title: 'Vendor Due Diligence',
    description:
      'Run consistent, proportionate due diligence on new and existing suppliers - from lightweight questionnaires to deep review for your most critical vendors.',
  },
  {
    icon: Layers,
    title: 'Supplier Tiering & Criticality',
    description:
      'Map and tier suppliers by criticality, data access, and business impact, so effort is focused where a compromise would hurt you most.',
  },
  {
    icon: ScrollText,
    title: 'Contractual Security Requirements',
    description:
      'Define the security clauses, SLAs, and right-to-audit provisions that should sit in every supplier contract, and embed them into procurement.',
  },
  {
    icon: Eye,
    title: 'Continuous Monitoring',
    description:
      'Move beyond point-in-time assessments with ongoing monitoring of supplier risk, breach notifications, and changes in their security posture.',
  },
  {
    icon: Share2,
    title: 'Fourth-Party & Concentration Risk',
    description:
      'Understand the dependencies behind your dependencies - the shared platforms and sub-processors that create systemic, concentrated risk.',
  },
];

const benefits = [
  'Clear visibility of the risk your suppliers introduce',
  'Faster, more consistent vendor onboarding',
  'Evidence for customer and investor due diligence',
  'Reduced exposure to supply chain and third-party attacks',
  'Alignment with NIS2, DORA, and ISO 27001 supplier controls',
  'Confidence that critical dependencies are actively managed',
];

const process = [
  {
    step: '01',
    title: 'Map the Supply Chain',
    description:
      'We build an inventory of your vendors, partners, and sub-processors, and map which systems and data each one can reach.',
  },
  {
    step: '02',
    title: 'Assess & Tier',
    description:
      'We risk-rate suppliers by criticality, data access, and posture, focusing scrutiny on the relationships that matter most.',
  },
  {
    step: '03',
    title: 'Remediate & Contract',
    description:
      'We close the highest-priority gaps, agree remediation with suppliers, and embed security requirements into contracts and onboarding.',
  },
  {
    step: '04',
    title: 'Monitor Continuously',
    description:
      'We establish an ongoing assurance cadence so supplier risk is re-evaluated as relationships, threats, and regulations evolve.',
  },
];

export default function SupplyChainRiskPage() {
  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Supply Chain Risk',
    description:
      'Manage third-party and supply chain risk with assessment, due diligence, contractual controls, and continuous monitoring.',
    url: 'https://thecisonetwork.com/services/supply-chain-risk',
    provider: {
      '@type': 'Organization',
      name: 'The CISO Network',
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }}
      />
      <PageHero
        title="Supply Chain Risk"
        subtitle="Your security is only as strong as your weakest vendor. We help you assess, prioritise, and continuously manage third-party and supply chain risk."
        eyebrow="Our Services"
        backgroundImage={getServiceCard('supply-chain-risk')?.image}
        backgroundAlt={getServiceCard('supply-chain-risk')?.imageAlt}
        seoTitle="Supply Chain & Third-Party Risk | The CISO Network"
        seoDescription="Fractional CISO supply chain risk services - third-party risk assessment, vendor due diligence, supplier tiering, contractual controls, and continuous monitoring."
      />

      {/* Overview */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-6">
                Your Risk Extends Beyond Your Perimeter
              </h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  Some of the most damaging breaches of recent years did not start inside the
                  victim&apos;s network - they arrived through a trusted supplier. Your attack
                  surface now includes every vendor, platform, and sub-processor you depend on.
                </p>
                <p>
                  Hidden exposure in third-party relationships has become a board-level concern and
                  a due-diligence red flag. Enterprise buyers and investors increasingly expect
                  evidence that you understand and manage the risk your supply chain carries.
                </p>
                <p>
                  Our fractional CISOs bring structure to third-party risk - assessing suppliers,
                  focusing effort on the ones that matter, and building an assurance process that
                  keeps pace with new relationships and new regulation.
                </p>
              </div>
            </div>
            <div className="bg-background rounded-2xl p-8 border border-border">
              <div className="flex items-center gap-4 mb-6 justify-center">
                <div className="h-12 w-12 rounded-xl bg-teal flex items-center justify-center">
                  <Boxes className="h-6 w-6 text-accent-foreground" />
                </div>
                <h3 className="text-xl font-heading font-bold text-foreground">
                  When You Need This
                </h3>
              </div>
              <div className="flex justify-center">
                <ul className="space-y-4 inline-block text-left">
                  {[
                    'You have no clear inventory of your vendors and their access',
                    'Supplier security is reviewed inconsistently, if at all',
                    'A key supplier suffered an incident that affected you',
                    'Customers or investors are asking about third-party risk',
                    'You need to meet NIS2, DORA, or ISO 27001 supplier requirements',
                    'You depend heavily on a small number of critical vendors',
                    'Procurement moves faster than security can assess',
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-muted-foreground">
                      <CheckCircle className="h-5 w-5 text-teal flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Offerings */}
      <section className="py-24 bg-background">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              Supply Chain Risk Services
            </h2>
            <p className="text-muted-foreground max-w-3xl mx-auto">
              Structured third-party risk management, from first assessment to continuous assurance
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {offerings.map((offering, i) => (
              <div key={i} className="bg-white rounded-2xl p-8 border border-border">
                <div className="h-12 w-12 rounded-xl bg-teal/10 flex items-center justify-center mb-6">
                  <offering.icon className="h-6 w-6 text-teal" />
                </div>
                <h3 className="text-xl font-heading font-bold text-foreground mb-3">
                  {offering.title}
                </h3>
                <p className="text-muted-foreground">{offering.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <div className="grid sm:grid-cols-2 gap-6">
                {benefits.map((benefit, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="h-8 w-8 rounded-full bg-teal/10 flex items-center justify-center flex-shrink-0">
                      <span className="text-teal font-bold text-sm">{i + 1}</span>
                    </div>
                    <p className="text-muted-foreground">{benefit}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-6">
                The Benefits of Managed Supply Chain Risk
              </h2>
              <p className="text-muted-foreground mb-6">
                Organisations that manage third-party risk well onboard suppliers faster, pass
                customer security reviews more easily, and are far less likely to be caught out by a
                vendor&apos;s breach.
              </p>
              <div className="p-6 bg-background rounded-xl border border-border">
                <h4 className="font-heading font-semibold text-foreground mb-3">
                  Typical Engagement
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Duration</span>
                    <span className="text-foreground">4-8 weeks</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Deliverable</span>
                    <span className="text-foreground">Third-party risk programme</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Output</span>
                    <span className="text-foreground">Prioritised, monitored supplier risk</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-24 bg-background">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              Our Approach
            </h2>
            <p className="text-muted-foreground max-w-3xl mx-auto">
              A practical methodology for getting control of third-party risk
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((step, i) => (
              <div key={i} className="relative">
                <div className="text-6xl font-heading font-bold text-teal/10 mb-4">{step.step}</div>
                <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
                  {step.title}
                </h3>
                <p className="text-muted-foreground text-sm">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Deliverables */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-8 text-center">
              Typical Deliverables
            </h2>
            <div className="space-y-4">
              {[
                {
                  title: 'Third-Party Risk Register',
                  description:
                    'A maintained inventory of suppliers with risk ratings, data access, and assessment status',
                },
                {
                  title: 'Vendor Tiering & Criticality Map',
                  description:
                    'Suppliers classified by business impact and access, so assurance effort is focused where it counts',
                },
                {
                  title: 'Due Diligence & Assessment Framework',
                  description:
                    'Proportionate questionnaires and review criteria for onboarding and periodic reassessment',
                },
                {
                  title: 'Supplier Security Requirements',
                  description:
                    'Standard contract clauses, SLAs, and right-to-audit provisions ready to embed into procurement',
                },
                {
                  title: 'Continuous Monitoring & Reporting Cadence',
                  description:
                    'An operating rhythm for ongoing supplier assurance, breach response, and board-level reporting',
                },
              ].map((deliverable, i) => (
                <div key={i} className="flex items-start gap-4 p-4 bg-background rounded-xl">
                  <div className="h-6 w-6 rounded-full bg-teal flex items-center justify-center flex-shrink-0 mt-0.5">
                    <CheckCircle className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <h4 className="font-heading font-semibold text-foreground">
                      {deliverable.title}
                    </h4>
                    <p className="text-muted-foreground text-sm">{deliverable.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-24 bg-background">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal/10 text-teal text-sm font-medium mb-6">
                <Activity className="h-4 w-4" />
                Complementary Services
              </div>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-6">
                Third-Party Risk Is Part of the Bigger Picture
              </h2>
              <p className="text-muted-foreground mb-6">
                Supply chain risk is most effective when connected to your wider risk assessment and
                compliance obligations. Our fractional CISOs fold third-party risk into a single,
                coherent security programme.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/services/risk-assessment"
                  className="inline-flex items-center text-teal font-medium hover:underline"
                >
                  Risk Assessment <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
                <Link
                  to="/services/compliance"
                  className="inline-flex items-center text-teal font-medium hover:underline"
                >
                  Compliance &amp; Governance <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </div>
            </div>
            <div className="bg-white rounded-2xl p-8 border border-border">
              <div className="h-12 w-12 rounded-xl bg-amber-100 flex items-center justify-center mb-6">
                <Clock className="h-6 w-6 text-amber-600" />
              </div>
              <h3 className="text-xl font-heading font-bold text-foreground mb-4">
                Supply Chain Risk Snapshot
              </h3>
              <p className="text-muted-foreground mb-6">
                Not sure where your third-party exposure lies? Our rapid snapshot inventories your
                key suppliers, tiers them by criticality, and highlights the relationships that need
                attention first.
              </p>
              <div className="space-y-4">
                <div className="flex justify-between py-2 border-b border-border">
                  <span className="text-muted-foreground">Duration</span>
                  <span className="font-medium text-foreground">1-2 weeks</span>
                </div>
                <div className="flex justify-between py-2 border-b border-border">
                  <span className="text-muted-foreground">Deliverable</span>
                  <span className="font-medium text-foreground">Supplier risk snapshot</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-muted-foreground">Output</span>
                  <span className="font-medium text-foreground">Prioritised action plan</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 gradient-navy cta-photo">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-primary-foreground mb-6">
            Secure Your Supply Chain
          </h2>
          <p className="text-primary-foreground/60 text-lg max-w-3xl mx-auto mb-10">
            Book a discovery call to discuss your third-party risk and how our{' '}
            <Link to="/what-is-a-fractional-ciso" className="text-teal hover:underline">
              fractional CISOs
            </Link>{' '}
            can help you manage it.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="btn-hero">
              Book a Discovery Call <ArrowRight className="ml-2 h-4 w-4" />
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
